/**
 * Phase 20 / Task 20.7 — the four-step wizard: upload → mapping → preview → run.
 *
 * ── Step 4 (slice 2) ───────────────────────────────────────────────────────
 * `POST …/commit` answers 202 and the import runs in the background (DEC-282);
 * `RunStep` polls its progress and shows the result, the failure, and undo.
 * A 409 `IMPORT_COMMIT_ALREADY_RUNNING` is a live commit on this batch — a
 * double click, or a second tab — and the wizard attaches to it rather than
 * reporting an error. Re-committing a finished batch is a 202 no-op.
 *
 * ── Leaving while it runs ──────────────────────────────────────────────────
 * §80 says the page cannot be left during an import. With the commit in the
 * background, leaving no longer stops or breaks anything — it only loses the
 * screen. So the guard asks rather than forbids: the app's own navigation and
 * sign-out confirm first, and the browser's leave-page prompt covers reload,
 * close and a typed URL (`lib/navigation-guard.ts`). The Back button cannot be
 * intercepted under this app's router; for that, and for anyone who confirms,
 * the running batch is remembered for the tab and the wizard re-opens on it.
 *
 * ── Re-import mode ─────────────────────────────────────────────────────────
 * §80's `[ Update existing ] / [ Skip existing — new only ]` toggle sits beside
 * the Import button on the preview step. UPDATE_ON_MATCH is the default (AC-4).
 *
 * ── Test run (#517, DEC-267) ───────────────────────────────────────────────
 * `[ Test run ]` commits with `isDryRun: true`: every row goes through the real
 * writer and is rolled back, so the result shows what WOULD happen. The batch
 * can then be imported for real from the result screen, which commits again
 * with `isDryRun: false` — sent explicitly, see `commitImport`.
 *
 * ── A 403 at commit ────────────────────────────────────────────────────────
 * The page is already limited to roles that may import (DEC-256), so the one
 * refusal a hauler can reach here is the rate tier: a Dispatch Manager
 * committing T13/T14. The preview warns first; if the commit is refused anyway
 * (a role matrix this client has not caught up with), the error names the rate
 * sheets and who can import them instead of a bare "permission denied".
 */

import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiOpts } from '@lodex-pro/web-shared';

import { useNavigationGuard } from '../../lib/navigation-guard';
import { COMMIT_ALREADY_RUNNING, ImportApiError, commitImport } from './api';
import { rateFamiliesIn, rateRefusalMessage } from './rate-tier';
import { MappingStep } from './MappingStep';
import { PreviewStep } from './PreviewStep';
import { forgetRunningBatch, recallRunningBatch, rememberRunningBatch } from './run-state';
import { RunStep } from './RunStep';
import type { CommitMode, UploadResult } from './types';
import { ActionButton, Notice, Panel, muted } from './ui';
import { UploadStep } from './UploadStep';

type Step = 'upload' | 'mapping' | 'preview' | 'run';

export const LEAVE_WHILE_RUNNING =
  'Your import is still running. Leaving will not stop it — come back to Settings → Import data to see how it finished. Leave this page?';

interface CommitArgs {
  readonly batchId: string;
  readonly mode: CommitMode;
  readonly isDryRun: boolean;
}

export function ImportWizard(props: {
  /** False for a role the commit refuses rate templates to (DEC-256). */
  readonly canImportRates: boolean;
}): React.JSX.Element {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  // An import this tab started and then left while it ran: open on it again.
  const [recalled] = React.useState<string | null>(recallRunningBatch);
  const [step, setStep] = React.useState<Step>(recalled === null ? 'upload' : 'run');
  const [uploaded, setUploaded] = React.useState<UploadResult | null>(null);
  const [runBatchId, setRunBatchId] = React.useState<string | null>(recalled);
  const [mode, setMode] = React.useState<CommitMode>('UPDATE_ON_MATCH');
  const [live, setLive] = React.useState(false);

  const batchId = uploaded?.batchId ?? null;

  const commit = useMutation<string, Error, CommitArgs>({
    mutationFn: async ({ batchId: id, mode: m, isDryRun }) => {
      try {
        await commitImport(apiOpts, id, m, isDryRun);
      } catch (err: unknown) {
        // Already running: watch that one. Anything else is a real refusal.
        if (err instanceof ImportApiError && err.code === COMMIT_ALREADY_RUNNING) return id;
        if (err instanceof ImportApiError && err.status === 403) {
          const families = (uploaded?.sheets ?? []).flatMap((s) =>
            s.staged && s.entityFamily !== null ? [s.entityFamily] : [],
          );
          throw new ImportApiError(403, err.code, rateRefusalMessage(rateFamiliesIn(families)));
        }
        throw err;
      }
      return id;
    },
    onSuccess: (id) => {
      rememberRunningBatch(id);
      // A retry after FAILED reuses the batch id; the cached FAILED status has
      // stopped polling and would be shown again without this.
      void qc.resetQueries({ queryKey: ['imports', id, 'commit-status'] });
      setRunBatchId(id);
    },
  });

  useNavigationGuard(live || commit.isPending ? LEAVE_WHILE_RUNNING : null);

  const startCommit = (id: string, m: CommitMode, isDryRun: boolean): void => {
    setStep('run');
    commit.mutate({ batchId: id, mode: m, isDryRun });
  };

  const restart = (): void => {
    if (batchId !== null) qc.removeQueries({ queryKey: ['imports', batchId] });
    if (runBatchId !== null) qc.removeQueries({ queryKey: ['imports', runBatchId] });
    forgetRunningBatch();
    commit.reset();
    setLive(false);
    setRunBatchId(null);
    setUploaded(null);
    setStep('upload');
  };

  if (step === 'run') {
    if (commit.isPending) {
      return <Panel>Starting the import…</Panel>;
    }
    if (commit.isError) {
      const { batchId: id, mode: m, isDryRun } = commit.variables;
      return (
        <Panel>
          <Notice tone="error" role="alert">
            {commit.error.message}
          </Notice>
          <p style={{ ...muted, marginTop: 12 }}>
            Rows that were already written are kept. Running the import again picks up where it
            stopped and does not write any row twice.
          </p>
          <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
            <ActionButton variant="primary" onClick={(): void => startCommit(id, m, isDryRun)}>
              {isDryRun ? 'Try the test run again' : 'Try the import again'}
            </ActionButton>
            {batchId === id ? (
              <ActionButton onClick={(): void => setStep('preview')}>
                Back to the preview
              </ActionButton>
            ) : (
              <ActionButton onClick={restart}>Start another import</ActionButton>
            )}
          </div>
        </Panel>
      );
    }
    if (runBatchId !== null) {
      return (
        <RunStep
          batchId={runBatchId}
          mode={mode}
          onLiveChange={setLive}
          onSettled={forgetRunningBatch}
          onRetry={(m, isDryRun): void => startCommit(runBatchId, m, isDryRun)}
          onImportForReal={(m): void => startCommit(runBatchId, m, false)}
          onBackToPreview={batchId === runBatchId ? (): void => setStep('preview') : undefined}
          onRestart={restart}
        />
      );
    }
  }

  if (step === 'mapping' && batchId !== null) {
    return (
      <MappingStep
        batchId={batchId}
        onBack={restart}
        onApplied={(): void => {
          // The mapping just changed, so any earlier preview is stale.
          qc.removeQueries({ queryKey: ['imports', batchId, 'preview'] });
          setStep('preview');
        }}
      />
    );
  }

  if (step === 'preview' && batchId !== null) {
    return (
      <PreviewStep
        batchId={batchId}
        mode={mode}
        onModeChange={setMode}
        canImportRates={props.canImportRates}
        onBack={(): void => setStep('mapping')}
        onRestart={restart}
        onImport={(): void => startCommit(batchId, mode, false)}
        onTestRun={(): void => startCommit(batchId, mode, true)}
      />
    );
  }

  return (
    <UploadStep
      uploaded={uploaded}
      onUploaded={setUploaded}
      onContinue={(): void => setStep('mapping')}
      onReset={restart}
    />
  );
}
